import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Utensils, Calendar, Clock, Image as ImageIcon,
  Euro, Star, RefreshCw, Wind, Snowflake, Tag
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SpecialMenusManagement = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, menu: null });
  const [formData, setFormData] = useState({
    name: '',
    name_pt: '',
    description: '',
    description_pt: '',
    price: '',
    original_price: '',
    image: '',
    is_active: true,
    is_featured: true,
    source_type: 'restaurant',
    restaurant_id: '',
    included_items_text: '',  // textarea: each line "qty x item name"
    available_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    available_hours_start: '',
    available_hours_end: ''
  });
  const [restaurants, setRestaurants] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/restaurants`).then(r => r.ok ? r.json() : []).then(setRestaurants).catch(() => {});
  }, []);

  const fetchMenus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/special-menus/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMenus(data);
      }
    } catch (error) {
      console.error('Error fetching menus:', error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  // Convert "2x Hamburguer\n1x Coca-cola" → [{name, quantity}, ...]
  const parseIncludedItems = (text) => {
    return (text || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const m = line.match(/^(\d+)\s*[x×]\s*(.+)$/i);
        if (m) return { quantity: parseInt(m[1], 10), name: m[2].trim() };
        return { quantity: 1, name: line };
      });
  };
  const stringifyIncludedItems = (arr) => {
    if (!Array.isArray(arr)) return '';
    return arr.map((i) => `${i.quantity || 1}x ${i.name || ''}`).join('\n');
  };

  const dayLabels = {
    monday: language === 'pt' ? 'Seg' : 'Mon',
    tuesday: language === 'pt' ? 'Ter' : 'Tue',
    wednesday: language === 'pt' ? 'Qua' : 'Wed',
    thursday: language === 'pt' ? 'Qui' : 'Thu',
    friday: language === 'pt' ? 'Sex' : 'Fri',
    saturday: language === 'pt' ? 'Sáb' : 'Sat',
    sunday: language === 'pt' ? 'Dom' : 'Sun'
  };

  const handleSave = async () => {
    if (!formData.name || !formData.price) {
      toast.error(language === 'pt' ? 'Preencha o nome e o preço' : 'Fill in name and price');
      return;
    }
    if (!formData.restaurant_id) {
      toast.error(language === 'pt' ? 'Escolha um restaurante para o menu funcionar no checkout' : 'Pick a restaurant so the menu works at checkout');
      return;
    }
    try {
      const url = editDialog.menu 
        ? `${API_URL}/api/admin/special-menus/${editDialog.menu.menu_id}`
        : `${API_URL}/api/admin/special-menus`;
      
      const res = await fetch(url, {
        method: editDialog.menu ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price) || 0,
          original_price: formData.original_price ? parseFloat(formData.original_price) : null,
          available_hours_start: formData.available_hours_start || null,
          available_hours_end: formData.available_hours_end || null,
          restaurant_id: formData.restaurant_id || null,
          included_items: parseIncludedItems(formData.included_items_text),
        })
      });

      if (res.ok) {
        toast.success(language === 'pt' ? 'Menu salvo!' : 'Menu saved!');
        setEditDialog({ open: false, menu: null });
        fetchMenus();
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao salvar' : 'Error saving');
    }
  };

  const handleDelete = async (menuId) => {
    if (!window.confirm(language === 'pt' ? 'Apagar este menu?' : 'Delete this menu?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/special-menus/${menuId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success(language === 'pt' ? 'Menu apagado!' : 'Menu deleted!');
        fetchMenus();
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao apagar' : 'Error deleting');
    }
  };

  const openEditDialog = (menu = null) => {
    if (menu) {
      setFormData({
        name: menu.name || '',
        name_pt: menu.name_pt || '',
        description: menu.description || '',
        description_pt: menu.description_pt || '',
        price: menu.price?.toString() || '',
        original_price: menu.original_price?.toString() || '',
        image: menu.image || '',
        is_active: menu.is_active ?? true,
        is_featured: menu.is_featured ?? true,
        source_type: menu.source_type || 'restaurant',
        restaurant_id: menu.restaurant_id || '',
        included_items_text: stringifyIncludedItems(menu.included_items),
        available_days: menu.available_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
        available_hours_start: menu.available_hours_start || '',
        available_hours_end: menu.available_hours_end || ''
      });
    } else {
      setFormData({
        name: '',
        name_pt: '',
        description: '',
        description_pt: '',
        price: '',
        original_price: '',
        image: '',
        is_active: true,
        is_featured: true,
        source_type: 'restaurant',
        restaurant_id: '',
        included_items_text: '',
        available_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
        available_hours_start: '',
        available_hours_end: ''
      });
    }
    setEditDialog({ open: true, menu });
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      available_days: prev.available_days.includes(day)
        ? prev.available_days.filter(d => d !== day)
        : [...prev.available_days, day]
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataUpload
        });
        if (res.ok) {
          const data = await res.json();
          setFormData(prev => ({ ...prev, image: `${API_URL}${data.url}` }));
          toast.success(language === 'pt' ? 'Imagem carregada!' : 'Image uploaded!');
        }
      } catch (err) {
        toast.error(language === 'pt' ? 'Erro ao carregar' : 'Upload failed');
      }
    }
  };

  return (
    <Card className="rounded-2xl">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Utensils size={18} className="text-[#FF6B35]" />
            {language === 'pt' ? 'Menus Especiais do Dia' : 'Daily Special Menus'}
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchMenus} className="rounded-full">
              <RefreshCw size={14} />
            </Button>
            <Button size="sm" onClick={() => openEditDialog()} className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">
              <Plus size={14} className="mr-1" />
              {language === 'pt' ? 'Novo Menu' : 'New Menu'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-[#9FA2B4]">
            {language === 'pt' ? 'Carregando...' : 'Loading...'}
          </div>
        ) : menus.length === 0 ? (
          <div className="text-center py-8 text-[#9FA2B4]">
            <Utensils size={40} className="mx-auto mb-2" />
            <p>{language === 'pt' ? 'Nenhum menu especial criado' : 'No special menus created'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {menus.map(menu => (
              <div key={menu.menu_id} className="border border-[#004E64]/10 rounded-xl overflow-hidden">
                {menu.image && (
                  <div className="h-32 overflow-hidden">
                    <img src={menu.image} alt={menu.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-[#004E64]">{menu.name}</h3>
                      <p className="text-xs text-[#9FA2B4]">{menu.name_pt}</p>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {menu.is_active ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">Ativo</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700 text-xs">Inativo</Badge>
                      )}
                      {menu.is_featured && (
                        <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                          <Star size={10} className="mr-1" />
                          Destaque
                        </Badge>
                      )}
                      {menu.source_type === 'shisha' && (
                        <Badge className="bg-purple-100 text-purple-700 text-xs">
                          <Wind size={10} className="mr-1" />
                          Shisha
                        </Badge>
                      )}
                      {menu.source_type === 'ice' && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">
                          <Snowflake size={10} className="mr-1" />
                          Gelo
                        </Badge>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-[#9FA2B4] line-clamp-2 mb-3">{menu.description}</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xl font-bold text-[#FF6B35]">€{menu.price?.toFixed(2)}</span>
                      {menu.original_price && (
                        <span className="text-sm text-[#9FA2B4] line-through ml-2">€{menu.original_price?.toFixed(2)}</span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEditDialog(menu)} className="h-8 w-8">
                        <Edit size={14} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleDelete(menu.menu_id)} className="h-8 w-8 text-red-500">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {Object.keys(dayLabels).map(day => (
                      <span 
                        key={day} 
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          menu.available_days?.includes(day) 
                            ? 'bg-[#FF6B35]/10 text-[#FF6B35]' 
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {dayLabels[day]}
                      </span>
                    ))}
                  </div>
                  {menu.available_hours_start && menu.available_hours_end && (
                    <div className="flex items-center gap-1 mt-1.5 text-xs text-[#2EC4B6]">
                      <Clock size={12} />
                      {menu.available_hours_start} - {menu.available_hours_end}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, menu: null })}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editDialog.menu 
                ? (language === 'pt' ? 'Editar Menu Especial' : 'Edit Special Menu')
                : (language === 'pt' ? 'Novo Menu Especial' : 'New Special Menu')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Nome (EN)' : 'Name (EN)'}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 rounded-xl"
                  placeholder="Menu of the Day"
                />
              </div>
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Nome (PT)' : 'Name (PT)'}</Label>
                <Input
                  value={formData.name_pt}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_pt: e.target.value }))}
                  className="mt-1 rounded-xl"
                  placeholder="Menu do Dia"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">{language === 'pt' ? 'Descrição (EN)' : 'Description (EN)'}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1 rounded-xl"
                rows={3}
                placeholder="Includes: Main dish, Drink, Dessert"
              />
            </div>

            <div>
              <Label className="text-xs">{language === 'pt' ? 'Descrição (PT)' : 'Description (PT)'}</Label>
              <Textarea
                value={formData.description_pt}
                onChange={(e) => setFormData(prev => ({ ...prev, description_pt: e.target.value }))}
                className="mt-1 rounded-xl"
                rows={3}
                placeholder="Inclui: Prato principal, Bebida, Sobremesa"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Preço (€)' : 'Price (€)'}</Label>
                <Input
                  type="number"
                  step="0.50"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  className="mt-1 rounded-xl"
                  placeholder="12.50"
                />
              </div>
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Preço Original (€)' : 'Original Price (€)'}</Label>
                <Input
                  type="number"
                  step="0.50"
                  value={formData.original_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, original_price: e.target.value }))}
                  className="mt-1 rounded-xl"
                  placeholder="18.00"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">{language === 'pt' ? 'Imagem' : 'Image'}</Label>
              <div className="mt-1 space-y-2">
                {formData.image && (
                  <img src={formData.image} alt="Preview" className="w-full h-32 object-cover rounded-xl" />
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">{language === 'pt' ? 'Tipo de Serviço' : 'Service Type'}</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {[
                  { value: 'restaurant', label: language === 'pt' ? 'Restaurante' : 'Restaurant', icon: Utensils, color: 'bg-[#FF6B35]' },
                  { value: 'shisha', label: 'Shisha', icon: Wind, color: 'bg-purple-500' },
                  { value: 'ice', label: language === 'pt' ? 'Gelo' : 'Ice', icon: Snowflake, color: 'bg-blue-500' },
                  { value: 'general', label: language === 'pt' ? 'Geral' : 'General', icon: Tag, color: 'bg-[#2EC4B6]' }
                ].map(opt => (
                  <Button
                    key={opt.value}
                    type="button"
                    size="sm"
                    variant={formData.source_type === opt.value ? 'default' : 'outline'}
                    className={`rounded-full ${formData.source_type === opt.value ? opt.color : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, source_type: opt.value }))}
                    data-testid={`source-type-${opt.value}`}
                  >
                    <opt.icon size={14} className="mr-1" />
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">{language === 'pt' ? 'Dias Disponíveis' : 'Available Days'}</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {Object.entries(dayLabels).map(([day, label]) => (
                  <Button
                    key={day}
                    type="button"
                    size="sm"
                    variant={formData.available_days.includes(day) ? 'default' : 'outline'}
                    className={`rounded-full ${formData.available_days.includes(day) ? 'bg-[#FF6B35]' : ''}`}
                    onClick={() => toggleDay(day)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Linked restaurant + included items */}
            <div>
              <Label className="text-xs">{language === 'pt' ? 'Restaurante (para checkout)' : 'Restaurant (for checkout)'}</Label>
              <select
                value={formData.restaurant_id}
                onChange={(e) => setFormData(prev => ({ ...prev, restaurant_id: e.target.value }))}
                className="w-full mt-1 rounded-xl border border-[#004E64]/10 bg-white p-2 text-sm"
                data-testid="special-menu-restaurant-select"
              >
                <option value="">{language === 'pt' ? '— Sem restaurante associado —' : '— No restaurant linked —'}</option>
                {restaurants.map((r) => (
                  <option key={r.restaurant_id} value={r.restaurant_id}>{r.name}</option>
                ))}
              </select>
              <p className="text-[11px] text-[#9FA2B4] mt-1">
                {language === 'pt'
                  ? 'Define a que restaurante o pedido vai (POS + comissão).'
                  : 'Defines which restaurant the order goes to (POS + commission).'}
              </p>
            </div>

            <div>
              <Label className="text-xs">{language === 'pt' ? 'Itens incluídos' : 'Included items'}</Label>
              <p className="text-[11px] text-[#9FA2B4] mb-1">
                {language === 'pt'
                  ? 'Uma linha por item. Formato: "2x Hamburguer" ou só "Coca-cola".'
                  : 'One per line. Format: "2x Burger" or just "Coke".'}
              </p>
              <textarea
                value={formData.included_items_text}
                onChange={(e) => setFormData(prev => ({ ...prev, included_items_text: e.target.value }))}
                rows={5}
                className="w-full rounded-xl border border-[#004E64]/10 bg-white p-2 text-sm font-mono"
                placeholder={language === 'pt' ? '2x Hamburguer\n1x Batata Frita\n1x Refrigerante' : '2x Burger\n1x Fries\n1x Soda'}
                data-testid="special-menu-included-items"
              />
            </div>

            <div>
              <Label className="text-xs">{language === 'pt' ? 'Horário Disponível (opcional)' : 'Available Hours (optional)'}</Label>
              <p className="text-xs text-[#9FA2B4] mb-2">{language === 'pt' ? 'Deixe vazio para disponível o dia todo' : 'Leave empty for all day'}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Início' : 'Start'}</Label>
                  <Input
                    type="time"
                    value={formData.available_hours_start}
                    onChange={(e) => setFormData(prev => ({ ...prev, available_hours_start: e.target.value }))}
                    className="mt-1 rounded-xl"
                    data-testid="hours-start-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Fim' : 'End'}</Label>
                  <Input
                    type="time"
                    value={formData.available_hours_end}
                    onChange={(e) => setFormData(prev => ({ ...prev, available_hours_end: e.target.value }))}
                    className="mt-1 rounded-xl"
                    data-testid="hours-end-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-[#F4F1EA] rounded-xl">
              <div>
                <p className="font-medium text-[#004E64] text-sm">{language === 'pt' ? 'Ativo' : 'Active'}</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-[#F4F1EA] rounded-xl">
              <div>
                <p className="font-medium text-[#004E64] text-sm">{language === 'pt' ? 'Destaque na Homepage' : 'Featured on Homepage'}</p>
              </div>
              <Switch
                checked={formData.is_featured}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
              />
            </div>

            <Button onClick={handleSave} className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-xl">
              {language === 'pt' ? 'Salvar Menu' : 'Save Menu'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SpecialMenusManagement;
